import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Caption1,
  Field, Input, Link, MessageBar, MessageBarBody, Select, Spinner, Text, Textarea, makeStyles,
} from '@fluentui/react-components';
import { personHistoryInputSchema, type ChecklistItem, type PersonHistoryEntry, type PersonHistoryInput } from '@vigeo/shared';
import { PopupDialog, useSectionActive, KeyValueList, tokens, type KeyValueItem } from '@vigeo/ui';
import { appendPersonHistory, fetchPersonHistory } from '@/lib/api.js';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  note: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
});

const labels: Record<PersonHistoryInput['kind'], string> = {
  'historical-evidence': 'Historical evidence', addendum: 'Addendum',
  waiver: 'Policy waiver', 'waiver-revocation': 'Waiver revocation',
};
const sourceStatuses = {
  verifiedCurrent: 'Confirmed current on original record', onFileNeedsReview: 'On file — HR review needed',
  physicalCopyOnFile: 'Physical copy — HR review needed', historicalEvidenceOnFile: 'Historical evidence on file',
  currentActionRequired: 'Current action required', missing: 'Missing', notApplicable: 'Not applicable',
};

export interface PersonHistoryPanelProps {
  readonly personId: string;
  readonly canEdit: boolean;
  readonly requirements?: readonly Pick<ChecklistItem, 'requirementKey' | 'label'>[];
}

function entryLabel(entry: PersonHistoryEntry): string {
  return `${labels[entry.kind]} — ${entry.kind === 'historical-evidence' ? entry.source.recordId
    : entry.kind === 'waiver' ? entry.requirementKey : new Date(entry.recordedAt).toLocaleString()}`;
}

export function PersonHistoryPanel({ personId, canEdit, requirements = [] }: PersonHistoryPanelProps) {
  const sectionActive = useSectionActive();
  const styles = useStyles();
  const client = useQueryClient();
  const history = useQuery({ queryKey: ['person-history', personId], queryFn: () => fetchPersonHistory(personId) });
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PersonHistoryInput['kind']>('historical-evidence');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [targetId, setTargetId] = useState('');
  const [requirementKey, setRequirementKey] = useState('');
  const [sourceSystem, setSourceSystem] = useState<'legacy-dashboard' | 'sharepoint' | 'external-record'>('legacy-dashboard');
  const [sourceId, setSourceId] = useState('');
  const [sourceVersion, setSourceVersion] = useState('');
  const [sourceActor, setSourceActor] = useState('');
  const [sourceHash, setSourceHash] = useState('');
  const [sourceStatus, setSourceStatus] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [occurredOn, setOccurredOn] = useState('');
  const [expires, setExpires] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  /** The button that opened the dialog, so the keyboard goes back there (Safari on a Mac does not focus a button on click). */
  const opener = useRef<HTMLElement | null>(null);
  const save = useMutation({
    mutationFn: (input: PersonHistoryInput) => appendPersonHistory(personId, input),
    onSuccess: async () => {
      setOpen(false);
      await Promise.all([
        ['person-history', personId], ['checklist', personId], ['dashboard'], ['audit', personId],
      ].map((queryKey) => client.invalidateQueries({ queryKey })));
    },
  });
  const entries = history.data?.entries ?? [];
  const waiverOptions = history.data?.waiverOptions ?? [];
  const revoked = new Set(entries.flatMap((entry) => entry.kind === 'waiver-revocation' ? [entry.targetId] : []));
  const targets = entries.filter((entry) => kind === 'waiver-revocation'
    ? entry.kind === 'waiver' && !revoked.has(entry.id)
    : entry.kind === 'historical-evidence' || entry.kind === 'addendum');

  function begin(button?: HTMLElement) {
    if (button !== undefined) opener.current = button;
    setKind('historical-evidence'); setReason(''); setNote(''); setTargetId(''); setRequirementKey('');
    setSourceSystem('legacy-dashboard'); setSourceId(''); setSourceVersion(''); setSourceUrl('');
    setSourceActor(''); setSourceHash(''); setSourceStatus('');
    setOccurredOn(''); setExpires(''); setValidation(null); save.reset(); setOpen(true);
  }

  /** Anything different from what `begin` sets (typing a value and putting it back counts as nothing). */
  const typed = kind !== 'historical-evidence' || sourceSystem !== 'legacy-dashboard'
    || [reason, note, targetId, requirementKey, sourceId, sourceVersion, sourceUrl, sourceActor, sourceHash, sourceStatus, occurredOn, expires].some((value) => value !== '');

  function submit() {
    if (!canEdit) return;
    try {
      const fields = kind === 'historical-evidence' ? {
        source: { system: sourceSystem, recordId: sourceId, version: sourceVersion || null, url: sourceUrl || null,
          recordedBy: sourceActor || null, contentHash: sourceHash || null, recordedStatus: sourceStatus || null },
        occurredAt: `${occurredOn}T00:00:00.000Z`, note, requirementKey: requirementKey || null,
      } : kind === 'waiver' ? {
        requirementKey, gate: waiverOptions.find((option) => option.requirementKey === requirementKey)?.gate,
        expiresAt: new Date(expires).toISOString(),
      } : kind === 'addendum' ? { targetId, note } : { targetId };
      const parsed = personHistoryInputSchema.safeParse({ kind, reason, ...fields });
      if (!parsed.success) { setValidation(parsed.error.issues.map((issue) => issue.message).join(' · ')); return; }
      setValidation(null); save.mutate(parsed.data);
    } catch { setValidation('Enter a valid expiry date and complete the required fields.'); }
  }

  function facts(entry: PersonHistoryEntry): KeyValueItem[] {
    const items: KeyValueItem[] = [
      { label: 'Recorded', value: new Date(entry.recordedAt).toLocaleString() },
      { label: 'Recorded by', value: entry.recordedBy }, { label: 'Reason', value: entry.reason },
    ];
    if (entry.kind === 'historical-evidence') items.push(
      { label: 'Original record date', value: entry.occurredAt.slice(0, 10) },
      { label: 'Source', value: `${entry.source.system} · ${entry.source.recordId}` },
      { label: 'Source version', value: entry.source.version ?? 'Not recorded' },
      { label: 'Original recorded by', value: entry.source.recordedBy ?? 'Not recorded' },
      { label: 'Original source hash', value: entry.source.contentHash ?? 'Not recorded' },
      { label: 'Status on original record', value: entry.source.recordedStatus ? sourceStatuses[entry.source.recordedStatus] : 'Not recorded' },
      { label: 'Requirement', value: requirements.find((row) => row.requirementKey === entry.requirementKey)?.label ?? entry.requirementKey ?? 'General history' },
      { label: 'Source link', value: entry.source.url ? <Link href={entry.source.url} target="_blank" rel="noopener noreferrer">Open Vigeo SharePoint record</Link> : 'Not provided' },
    );
    if (entry.kind === 'waiver') items.push(
      { label: 'Requirement', value: requirements.find((row) => row.requirementKey === entry.requirementKey)?.label ?? entry.requirementKey },
      { label: 'Gate', value: 'First assignment' },
      { label: 'Expires', value: new Date(entry.expiresAt).toLocaleString() },
      { label: 'Decision', value: revoked.has(entry.id) ? 'Revoked — original decision retained' : 'Granted — current policy and expiry determine whether it applies' },
    );
    if ('targetId' in entry) items.push({ label: 'Original entry', value: entries.find((original) => original.id === entry.targetId) ? entryLabel(entries.find((original) => original.id === entry.targetId)!) : entry.targetId });
    return items;
  }

  return <section aria-label="History and decisions" className={styles.panel}>
    <div><Text weight="semibold">History and decisions</Text><br />
      <Caption1>Historical references preserve their original source. Addenda record corrections; they do not replace an earlier record. A policy waiver leaves the requirement unmet.</Caption1></div>
    {history.isPending ? <Spinner label="Loading history" size="small" /> : null}
    {history.isError ? <MessageBar intent="error"><MessageBarBody>{history.error.message}</MessageBarBody></MessageBar> : null}
    {history.isSuccess && entries.length === 0 ? <Caption1>No historical references or decisions recorded.</Caption1> : null}
    <Accordion multiple collapsible>
      {[...entries].reverse().map((entry) => <AccordionItem key={entry.id} value={entry.id}>
        <AccordionHeader>{entryLabel(entry)}</AccordionHeader>
        <AccordionPanel><div className={styles.panel}>
          <KeyValueList items={facts(entry)} />
          {'note' in entry ? <Text className={styles.note}>{entry.note}</Text> : null}
        </div></AccordionPanel>
      </AccordionItem>)}
    </Accordion>
    {canEdit ? <Button onClick={(event) => begin(event.currentTarget)} disabled={!history.isSuccess}>Add history or decision</Button> : null}
    {/*
      INTERIM, AND TOO LONG FOR A DIALOG. The pop-up rules (PG10) put a form of more than 5 fields in the page, under what opened it;
      a historical-evidence entry has 12. It stays a dialog, on the large size with a scrolling middle, until Julio decides where it
      goes (HR LEAD 3 has the question). Everything else the rules ask of a dialog applies now: Cancel left and the one blue button
      right, the keyboard on the first field, X/Esc/click outside asking "Discard what you typed?" when anything changed, nothing
      closing while it saves, and the keyboard back on the button that opened it. What is saved is unchanged (`submit` above).
      `sectionActive` is kept: the dialog closes while its section is not the one showing and comes back, with what was typed.
    */}
    <PopupDialog
      open={open && canEdit && sectionActive}
      title="Add history or decision"
      size="large"
      opener={opener}
      dirty={typed}
      busy={save.isPending}
      onClose={() => setOpen(false)}
      primary={{ label: 'Record entry', busyLabel: 'Saving…', type: 'submit', form: 'person-history-form' }}
    >
      <form id="person-history-form" className={styles.panel} onSubmit={(event) => { event.preventDefault(); submit(); }}>
          {validation || save.isError ? <MessageBar intent="error"><MessageBarBody>{validation ?? save.error?.message}</MessageBarBody></MessageBar> : null}
          <Field label="Record type"><Select value={kind} disabled={save.isPending} onChange={(_, data) => {
            setKind(data.value as PersonHistoryInput['kind']); setRequirementKey(''); setTargetId(''); setValidation(null);
          }}>{Object.entries(labels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select></Field>
          {kind === 'historical-evidence' ? <>
            <Field label="Original source"><Select value={sourceSystem} onChange={(_, data) => setSourceSystem(data.value as typeof sourceSystem)}>
              <option value="legacy-dashboard">Legacy HR dashboard</option><option value="sharepoint">SharePoint</option><option value="external-record">External record</option>
            </Select></Field>
            <Field label="Original record / packet ID" required><Input value={sourceId} onChange={(_, data) => setSourceId(data.value)} /></Field>
            <Field label="Original version"><Input value={sourceVersion} onChange={(_, data) => setSourceVersion(data.value)} /></Field>
            <Field label="Original recorded by"><Input value={sourceActor} onChange={(_, data) => setSourceActor(data.value)} /></Field>
            <Field label="Original source SHA-256"><Input value={sourceHash} onChange={(_, data) => setSourceHash(data.value)} /></Field>
            <Field label="Status on original record"><Select value={sourceStatus} onChange={(_, data) => setSourceStatus(data.value)}>
              <option value="">Not recorded</option>{Object.entries(sourceStatuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Select></Field>
            <Caption1>The source status records what the earlier system said. It does not mark today’s requirement complete.</Caption1>
            <Field label="Vigeo SharePoint source link"><Input type="url" value={sourceUrl} onChange={(_, data) => setSourceUrl(data.value)} /></Field>
            <Field label="Original record date" required><Input type="date" value={occurredOn} onChange={(_, data) => setOccurredOn(data.value)} /></Field>
            <Field label="Related requirement"><Select value={requirementKey} onChange={(_, data) => setRequirementKey(data.value)}>
              <option value="">General history</option>{requirements.map((row) => <option key={row.requirementKey} value={row.requirementKey}>{row.label}</option>)}
            </Select></Field>
          </> : null}
          {kind === 'addendum' || kind === 'waiver-revocation' ? <Field label="Original entry" required><Select value={targetId} onChange={(_, data) => setTargetId(data.value)}>
            <option value="">Choose an entry</option>{targets.map((entry) => <option key={entry.id} value={entry.id}>{entryLabel(entry)}</option>)}
          </Select></Field> : null}
          {kind === 'waiver' ? <>
            <Caption1>Only requirements whose current policy allows a waiver are offered. A waiver does not record completion or final HR clearance.</Caption1>
            <Field label="Waivable requirement" required><Select value={requirementKey} onChange={(_, data) => setRequirementKey(data.value)}>
              <option value="">Choose a requirement</option>{waiverOptions.map((option) => <option key={`${option.requirementKey}:${option.gate}`} value={option.requirementKey}>{option.label}{option.maxDays === null ? '' : ` (maximum ${option.maxDays} days)`}</option>)}
            </Select></Field>
            {waiverOptions.length === 0 ? <Caption1>No current requirement permits a waiver for this person and your role.</Caption1> : null}
            <Field label="Expires (local time)" required><Input type="datetime-local" value={expires} onChange={(_, data) => setExpires(data.value)} /></Field>
          </> : null}
          {kind === 'historical-evidence' || kind === 'addendum' ? <Field label={kind === 'addendum' ? 'Correction / additional information' : 'Historical evidence summary'} required>
            <Textarea value={note} onChange={(_, data) => setNote(data.value)} />
          </Field> : null}
          <Field label="Reason" required hint="At least 20 characters; explain the record or decision."><Textarea value={reason} onChange={(_, data) => setReason(data.value)} /></Field>
        </form>
    </PopupDialog>
  </section>;
}
